import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, ImageUp, Info, Mail, Palette, Server, Trash2, Inbox } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatFileSize, settingsBrandingSchema, settingsEmailSchema, settingsMailboxesSchema } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DescriptionList,
  ErrorState,
  Form,
  FormRootError,
  HtmlEditor,
  Input,
  InputField,
  Label,
  PageHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TextareaField,
  brandPalette,
  DEFAULT_PRIMARY,
  toast,
} from '@blimo/ui';

const SETTINGS_TABS = [
  { value: 'branding', label: 'Branding', icon: Palette },
  { value: 'email', label: 'E-Mail', icon: Mail },
  { value: 'postfaecher', label: 'Postfächer', icon: Inbox },
  { value: 'system', label: 'System', icon: Server },
];

const ASSETS = [
  {
    slug: 'logo-light',
    key: 'logo_light',
    label: 'Logo (hell)',
    hint: 'Für helle Flächen – Login, Seitenleiste, Portal-Kopfzeile. Querformat, transparenter Hintergrund.',
  },
  { slug: 'logo-dark', key: 'logo_dark', label: 'Logo (dunkel)', hint: 'Für das dunkle Farbschema. Ohne eigenes Bild wird das helle Logo verwendet.' },
  { slug: 'favicon', key: 'favicon', label: 'Favicon / Symbol', hint: 'Quadratisch (mind. 64 × 64 px); Browser-Tab und eingeklappte Navigation.' },
];

const ACCEPT = 'image/png,image/webp,image/jpeg,image/svg+xml';

/** `null` → '' for form fields. @param {Record<string, any>} values @param {string[]} fields */
const toForm = (values, fields) => Object.fromEntries(fields.map((f) => [f, values?.[f] ?? '']));
/** '' → null (= Standard) for the API. @param {Record<string, any>} values */
const toPayload = (values) => Object.fromEntries(Object.entries(values).map(([k, v]) => [k, typeof v === 'string' && v.trim() === '' ? null : v]));

/**
 * Administration › Einstellungen / Branding (F-014/F-015, `settings.manage`): Branding mit Live-Vorschau,
 * E-Mail (Absendername, Antwortadresse, Signatur), Funktionspostfächer, Systeminformationen (read-only).
 * Leere Felder = Standardwert (Server-Konfiguration); Bilder werden sofort gespeichert.
 */
export function SettingsPage() {
  const api = useApi();
  const query = useQuery(adminQueries.settings(api));
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const tab = SETTINGS_TABS.some((t) => t.value === search.tab) ? search.tab : 'branding';
  const [dirty, setDirty] = useState(/** @type {Record<string, boolean>} */ ({}));
  const markDirty = useCallback((name, value) => setDirty((d) => (d[name] === value ? d : { ...d, [name]: value })), []);
  const anyDirty = Object.values(dirty).some(Boolean);

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => anyDirty && current.pathname !== next.pathname,
    enableBeforeUnload: () => anyDirty,
    withResolver: true,
  });

  const setTab = (value) => navigate({ to: '.', search: (prev) => ({ ...prev, tab: value === 'branding' ? undefined : value }), replace: true });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Einstellungen / Branding"
        description="Erscheinungsbild beider Apps, E-Mail-Versand und Funktionspostfächer. Leere Felder verwenden den Standardwert."
      />

      {query.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-9 w-80" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList aria-label="Einstellungsbereiche">
            {SETTINGS_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                <t.icon className="size-4" aria-hidden="true" />
                {t.label}
                {dirty[t.value] && <span className="size-1.5 rounded-full bg-primary" aria-label="ungespeicherte Änderungen" />}
              </TabsTrigger>
            ))}
          </TabsList>
          {/* forceMount: Eingaben bleiben beim Tab-Wechsel erhalten */}
          <TabsContent value="branding" forceMount className="data-[state=inactive]:hidden">
            <BrandingTab settings={query.data} onDirty={(v) => markDirty('branding', v)} />
          </TabsContent>
          <TabsContent value="email" forceMount className="data-[state=inactive]:hidden">
            <EmailTab settings={query.data} onDirty={(v) => markDirty('email', v)} />
          </TabsContent>
          <TabsContent value="postfaecher" forceMount className="data-[state=inactive]:hidden">
            <MailboxesTab settings={query.data} onDirty={(v) => markDirty('postfaecher', v)} />
          </TabsContent>
          <TabsContent value="system" forceMount className="data-[state=inactive]:hidden">
            <SystemTab system={query.data.data.system} />
          </TabsContent>
        </Tabs>
      )}

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => !open && blocker.reset?.()}
        title="Ungespeicherte Änderungen verwerfen?"
        description="Sie haben Einstellungen geändert, aber noch nicht gespeichert. Beim Verlassen der Seite gehen diese Änderungen verloren."
        confirmLabel="Verwerfen und verlassen"
        cancelLabel="Weiter bearbeiten"
        destructive
        onConfirm={() => blocker.proceed?.()}
      />
    </div>
  );
}

/** Shared save logic: PUT one group, replace the cache, refresh public branding, reset the form to the saved state. */
function useSaveGroup(group, fields, form) {
  const api = useApi();
  const queryClient = useQueryClient();
  return form.handleSubmit(async (values) => {
    try {
      const body = await api.admin.settings.update({ [group]: toPayload(values) });
      queryClient.setQueryData(keys.admin.settings(), body);
      queryClient.invalidateQueries({ queryKey: keys.branding() });
      form.reset(toForm(body.data[group], fields));
      const warnings = (body.meta?.warnings ?? []).filter((w) => w.field?.startsWith(`${group}.`));
      if (warnings.length) toast.warning('Gespeichert – mit Hinweis', { description: warnings[0].message });
      else toast.success('Einstellungen gespeichert');
    } catch (error) {
      applyProblemToForm(
        // field errors come back as "branding.company_name" → form field "company_name"
        error?.errors ? { ...error, errors: Object.fromEntries(Object.entries(error.errors).map(([k, v]) => [k.replace(`${group}.`, ''), v])) } : error,
        form.setError,
        { fields },
      );
    }
  });
}

/** Reports the form's dirty state to the page (tab marker + leave guard). */
function useReportDirty(form, onDirty) {
  const isDirty = form.formState.isDirty;
  useEffect(() => onDirty(isDirty), [isDirty, onDirty]);
}

function SaveBar({ form, label = 'Speichern' }) {
  const { isDirty, isSubmitting } = form.formState;
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {isDirty && <span className="mr-auto text-xs text-muted-foreground">Ungespeicherte Änderungen</span>}
      <Button type="button" variant="outline" disabled={!isDirty || isSubmitting} onClick={() => form.reset()}>
        Verwerfen
      </Button>
      <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
        {label}
      </Button>
    </div>
  );
}

// --- Branding --------------------------------------------------------------------------------

const BRANDING_FIELDS = ['company_name', 'short_name', 'primary_color', 'welcome_text', 'imprint_url', 'privacy_url', 'support_phone', 'support_email'];

function BrandingTab({ settings, onDirty }) {
  const branding = settings.data.branding;
  const defaults = settings.meta?.defaults ?? {};
  const form = useForm({ resolver: zodResolver(settingsBrandingSchema), defaultValues: toForm(branding, BRANDING_FIELDS) });
  useReportDirty(form, onDirty);
  const onSubmit = useSaveGroup('branding', BRANDING_FIELDS, form);
  const watched = useWatch({ control: form.control });
  const color = /^#[0-9a-fA-F]{6}$/.test(watched.primary_color ?? '') ? watched.primary_color : (defaults['branding.primary_color'] ?? DEFAULT_PRIMARY);
  const serverWarnings = (settings.meta?.warnings ?? []).filter((w) => w.field?.startsWith('branding.'));

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="flex min-w-0 flex-col gap-4">
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate aria-label="Branding">
            <FormRootError />
            <Card>
              <CardHeader>
                <CardTitle>Firma</CardTitle>
                <CardDescription>Erscheint in Titeln, Fußzeilen, E-Mails (Kopf/Fuß) und als Alternativtext des Logos.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <InputField name="company_name" label="Firmenname" placeholder={defaults['branding.company_name'] ?? ''} maxLength={120} />
                <InputField
                  name="short_name"
                  label="Kurzname"
                  placeholder={defaults['branding.short_name'] ?? ''}
                  maxLength={40}
                  description="Browser-Tab, z. B. „BLIMO Portal“."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Primärfarbe</CardTitle>
                <CardDescription>Schaltflächen, Links, Fokusrahmen und Hervorhebungen in Verwaltung und Portal.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <ColorField form={form} fallback={defaults['branding.primary_color'] ?? DEFAULT_PRIMARY} />
                <ContrastHint color={color} />
                {serverWarnings.map((w) => (
                  <Alert key={w.code} variant="warning">
                    <AlertTriangle aria-hidden="true" />
                    <AlertTitle>Hinweis zur gespeicherten Farbe</AlertTitle>
                    <AlertDescription>{w.message}</AlertDescription>
                  </Alert>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Portal</CardTitle>
                <CardDescription>Begrüßungstext auf der Startseite des Kundenportals (reiner Text, Zeilenumbrüche bleiben erhalten).</CardDescription>
              </CardHeader>
              <CardContent>
                <TextareaField
                  name="welcome_text"
                  label="Begrüßungstext"
                  rows={3}
                  maxLength={1000}
                  placeholder="Willkommen in Ihrem Kundenportal – hier sehen Sie, was bei Ihren Immobilien ansteht."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rechtliches & Kontakt</CardTitle>
                <CardDescription>Links auf den Anmeldeseiten, im Portal-Profil und in der Portal-Fußzeile.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <InputField name="imprint_url" label="Impressum (URL)" type="url" inputMode="url" placeholder="https://…" />
                <InputField name="privacy_url" label="Datenschutzerklärung (URL)" type="url" inputMode="url" placeholder="https://…" />
                <InputField name="support_phone" label="Support-Telefon" type="tel" inputMode="tel" placeholder="+49 …" />
                <InputField name="support_email" label="Support-E-Mail" type="email" inputMode="email" placeholder="service@…" />
              </CardContent>
            </Card>
            <SaveBar form={form} />
          </form>
        </Form>

        <Card>
          <CardHeader>
            <CardTitle>Logos & Favicon</CardTitle>
            <CardDescription>
              PNG, WebP, JPEG oder SVG (SVG ohne Skripte/externe Verweise; wird bereinigt). Logos max. 1 MB, Favicon max. 256 KB. Änderungen werden sofort
              gespeichert; ohne eigenes Bild gilt das BLIMO-Logo.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            {ASSETS.map((asset) => (
              <AssetSlot key={asset.slug} asset={asset} current={branding[asset.key]} />
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start">
        <BrandingPreview
          color={color}
          companyName={watched.company_name || defaults['branding.company_name'] || 'BLIMO'}
          logoLight={branding.logo_light?.url ?? null}
          logoDark={branding.logo_dark?.url ?? branding.logo_light?.url ?? null}
        />
      </div>
    </div>
  );
}

/** Colour picker + hex input on the same form value. */
function ColorField({ form, fallback }) {
  const id = useId();
  return (
    <Controller
      control={form.control}
      name="primary_color"
      render={({ field, fieldState }) => {
        const valid = /^#[0-9a-fA-F]{6}$/.test(field.value ?? '');
        return (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-hex`}>Primärfarbe (Hex)</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Farbwähler Primärfarbe"
                value={valid ? field.value.toLowerCase() : fallback}
                onChange={(e) => field.onChange(e.target.value)}
                className="h-(--control-height) w-12 shrink-0 cursor-pointer rounded-md border bg-card p-1"
              />
              <Input
                id={`${id}-hex`}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                placeholder={fallback}
                maxLength={7}
                className="w-32 font-mono"
                aria-invalid={fieldState.invalid || undefined}
                aria-describedby={`${id}-hint`}
              />
              {field.value && (
                <Button type="button" variant="ghost" size="sm" onClick={() => field.onChange('')}>
                  Standard
                </Button>
              )}
            </div>
            <p id={`${id}-hint`} className={fieldState.error ? 'text-sm text-destructive' : 'text-xs text-muted-foreground'}>
              {fieldState.error?.message ?? `Leer = Standard (${fallback}).`}
            </p>
          </div>
        );
      }}
    />
  );
}

/** Live WCAG hint (presentation only – the server returns the authoritative warning after saving). */
function ContrastHint({ color }) {
  const p = brandPalette(color);
  const fmt = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="contrast-hint">
      <span>
        Kontrast Schrift auf Primärfarbe: weiß {fmt(p.contrast.white)}:1 · dunkel {fmt(p.contrast.dark)}:1
      </span>
      {p.passesAA ? (
        <Badge variant="success">WCAG AA erfüllt ({p.foreground === '#ffffff' ? 'weiße' : 'dunkle'} Schrift)</Badge>
      ) : (
        <Badge variant="warning">unter 4,5:1 – schlecht lesbar</Badge>
      )}
    </p>
  );
}

/** Mini preview of the portal header + button, light and dark, with the unsaved colour. */
function BrandingPreview({ color, companyName, logoLight, logoDark }) {
  const p = brandPalette(color);
  const vars = (dark) => ({
    '--primary': p.primary,
    '--primary-foreground': p.foreground,
    '--primary-text': dark ? p.textDark : p.textLight,
    '--ring': p.primary,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vorschau</CardTitle>
        <CardDescription>Portal-Kopfzeile und Schaltfläche mit den aktuellen (auch ungespeicherten) Werten.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3" data-testid="branding-preview">
        {[false, true].map((dark) => (
          <div
            key={dark ? 'dark' : 'light'}
            className={
              dark ? 'dark overflow-hidden rounded-lg border bg-background text-foreground' : 'overflow-hidden rounded-lg border bg-background text-foreground'
            }
            style={vars(dark)}
            aria-label={dark ? 'Vorschau dunkel' : 'Vorschau hell'}
            role="group"
          >
            <div className="flex h-11 items-center gap-2 border-b px-3">
              <PreviewLogo src={dark ? logoDark : logoLight} dark={dark} alt={companyName} />
              <span className="ml-auto size-6 rounded-full bg-muted" aria-hidden="true" />
            </div>
            <div className="flex flex-col gap-2 p-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-md bg-(--primary)/15 px-1.5 py-0.5 font-medium text-(--primary-text)">3 neu</span>
                <span className="text-(--primary-text) underline underline-offset-4">Link im Text</span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex h-8 items-center rounded-md px-3 text-sm font-medium"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  Speichern
                </span>
                <span className="inline-flex h-8 items-center rounded-md border px-3 text-sm">Abbrechen</span>
              </div>
            </div>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">Gilt nach dem Speichern für Verwaltung und Portal (auch auf den Anmeldeseiten).</p>
      </CardContent>
    </Card>
  );
}

function PreviewLogo({ src, dark, alt }) {
  if (src) return <img src={src} alt={alt} className="h-6 w-auto max-w-[10rem] object-contain" />;
  return <img src={dark ? '/brand/logo-dark.svg' : '/brand/logo.svg'} alt={alt} className="h-6 w-auto" />;
}

/** One image slot: preview, upload (immediate), remove with confirmation. */
function AssetSlot({ asset, current }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [confirm, setConfirm] = useState(false);
  const id = useId();

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.admin.settings() });
    queryClient.invalidateQueries({ queryKey: keys.branding() });
  };

  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.admin.settings.uploadAsset(asset.slug, file);
      await refresh();
      toast.success(`${asset.label} gespeichert`);
    } catch (e) {
      setError(e?.errors?.file?.[0] ?? getErrorMessage(e, 'Das Bild konnte nicht gespeichert werden.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    try {
      await api.admin.settings.removeAsset(asset.slug);
      await refresh();
      toast.success(`${asset.label} entfernt`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Das Bild konnte nicht entfernt werden.'));
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`asset-${asset.slug}`}>
      <Label htmlFor={id}>{asset.label}</Label>
      <div
        className={
          asset.slug === 'logo-dark'
            ? 'dark flex h-24 items-center justify-center rounded-lg border bg-background p-3'
            : 'flex h-24 items-center justify-center rounded-lg border bg-muted/40 p-3'
        }
      >
        {current?.url ? (
          <img src={current.url} alt={`${asset.label} (aktuell)`} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
            <ImageUp className="size-5" aria-hidden="true" />
            Standard (BLIMO)
          </span>
        )}
      </div>
      {current && (
        <p className="truncate text-xs text-muted-foreground" title={current.original_name ?? undefined}>
          {current.mime?.replace('image/', '').toUpperCase()} · {formatFileSize(current.size ?? 0)}
          {current.width ? ` · ${current.width} × ${current.height} px` : ''}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{asset.hint}</p>
      <input ref={inputRef} id={id} type="file" accept={ACCEPT} className="sr-only" onChange={(e) => upload(e.target.files?.[0])} disabled={busy} />
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" loading={busy} onClick={() => inputRef.current?.click()}>
          {current ? 'Ersetzen' : 'Hochladen'}
        </Button>
        {current && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setConfirm(true)} aria-label={`${asset.label} entfernen`}>
            <Trash2 className="size-4" aria-hidden="true" />
            Entfernen
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`${asset.label} entfernen?`}
        description="Danach wird wieder das mitgelieferte BLIMO-Bild verwendet."
        confirmLabel="Entfernen"
        destructive
        onConfirm={remove}
      />
    </div>
  );
}

// --- E-Mail ----------------------------------------------------------------------------------

const EMAIL_FIELDS = ['sender_name', 'reply_to', 'signature_html'];

function EmailTab({ settings, onDirty }) {
  const email = settings.data.email;
  const defaults = settings.meta?.defaults ?? {};
  const form = useForm({ resolver: zodResolver(settingsEmailSchema), defaultValues: toForm(email, EMAIL_FIELDS) });
  useReportDirty(form, onDirty);
  const onSubmit = useSaveGroup('email', EMAIL_FIELDS, form);
  const signatureId = useId();

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-4" noValidate aria-label="E-Mail">
        <FormRootError />
        <Card>
          <CardHeader>
            <CardTitle>Absender</CardTitle>
            <CardDescription>Gilt für alle ausgehenden E-Mails (Benachrichtigungen, Vorlagen, Einladungen).</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <InputField
              name="sender_name"
              label="Absendername"
              maxLength={100}
              placeholder={defaults['email.sender_name'] ?? ''}
              description="Leer = Name aus der Server-Konfiguration (MAIL_FROM_NAME)."
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${signatureId}-from`}>Absenderadresse</Label>
              <Input id={`${signatureId}-from`} value={email.from_address ?? ''} readOnly aria-readonly="true" className="bg-muted/40" />
              <p className="text-xs text-muted-foreground">Nur lesend – aus der Server-Konfiguration (MAIL_FROM_ADDRESS, SPF/DKIM).</p>
            </div>
            <InputField
              name="reply_to"
              label="Antwortadresse (Reply-To)"
              type="email"
              inputMode="email"
              placeholder="z. B. service@…"
              description="Antworten der Empfänger gehen an diese Adresse. Leer = an die Absenderadresse."
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Signatur</CardTitle>
            <CardDescription>
              Wird unter jede E-Mail gesetzt. Einfache Formatierung (fett, kursiv, Listen, Links); Skripte, Bilder und Stile entfernt der Server.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Controller
              control={form.control}
              name="signature_html"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <HtmlEditor
                    id={signatureId}
                    label="E-Mail-Signatur"
                    rows={6}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  {fieldState.error && <p className="text-sm text-destructive">{fieldState.error.message}</p>}
                </div>
              )}
            />
          </CardContent>
        </Card>
        <SaveBar form={form} />
      </form>
    </Form>
  );
}

// --- Funktionspostfächer -----------------------------------------------------------------------

const MAILBOX_FIELDS = ['management', 'ops'];

function MailboxesTab({ settings, onDirty }) {
  const defaults = settings.meta?.defaults ?? {};
  const form = useForm({ resolver: zodResolver(settingsMailboxesSchema), defaultValues: toForm(settings.data.mailboxes, MAILBOX_FIELDS) });
  useReportDirty(form, onDirty);
  const onSubmit = useSaveGroup('mailboxes', MAILBOX_FIELDS, form);
  const envHint = (key, name) => (defaults[key] ? `Leer = Wert aus ${name}: ${defaults[key]}` : `Leer = Wert aus ${name} (derzeit nicht gesetzt).`);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex max-w-3xl flex-col gap-4" noValidate aria-label="Funktionspostfächer">
        <FormRootError />
        <Alert variant="info">
          <Info aria-hidden="true" />
          <AlertDescription>
            Hier eingetragene Adressen haben Vorrang vor der Server-Konfiguration; die Umgebungsvariablen bleiben Rückfallwert.
          </AlertDescription>
        </Alert>
        <Card>
          <CardHeader>
            <CardTitle>Funktionspostfächer</CardTitle>
            <CardDescription>Empfänger interner Benachrichtigungen.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            <InputField
              name="management"
              label="Verwaltung"
              type="email"
              inputMode="email"
              placeholder={defaults['mailboxes.management'] ?? ''}
              description={`Eigentümer-Nachrichten, abgeschlossene Belegprüfungen, Kommentare, bestätigte Vollmachten. ${envHint('mailboxes.management', 'MANAGEMENT_MAILBOX')}`}
            />
            <InputField
              name="ops"
              label="Betrieb / IT"
              type="email"
              inputMode="email"
              placeholder={defaults['mailboxes.ops'] ?? defaults['mailboxes.management'] ?? ''}
              description={`Alarme zu Datenlieferungen (Import-Monitoring). ${envHint('mailboxes.ops', 'OPS_MAILBOX')} Ohne Wert: Verwaltungspostfach.`}
            />
          </CardContent>
        </Card>
        <SaveBar form={form} />
      </form>
    </Form>
  );
}

// --- System ----------------------------------------------------------------------------------

function SystemTab({ system }) {
  const integrations = system.integrations ?? {};
  const items = [
    { label: 'Anwendung', value: system.app_name },
    { label: 'Version', value: <span className="font-mono">{system.version}</span> },
    {
      label: 'Umgebung',
      value: (
        <span className="inline-flex items-center gap-2">
          {system.environment}
          {system.debug && <Badge variant="warning">Debug aktiv</Badge>}
        </span>
      ),
    },
    { label: 'PHP / Laravel', value: `${system.php_version} / ${system.laravel_version}` },
    { label: 'Zeitzone', value: system.timezone },
    { label: 'Mail-Versand', value: system.mail_transport, hint: system.mail_from_address ? `Absender ${system.mail_from_address}` : null },
    { label: 'Warteschlange', value: system.queue_connection },
    { label: 'Cache', value: system.cache_store },
    { label: 'Suche', value: system.search_driver },
    { label: 'Dokumentablage', value: system.documents_driver },
    {
      label: 'Datenlieferungen',
      value:
        integrations.feeds > 0 ? (
          <span className="inline-flex flex-wrap items-center gap-2">
            {integrations.feeds} Lieferungen
            {integrations.stale > 0 ? <Badge variant="warning">{integrations.stale} veraltet</Badge> : <Badge variant="success">aktuell</Badge>}
            {integrations.failed > 0 && <Badge variant="destructive">{integrations.failed} fehlgeschlagen</Badge>}
          </span>
        ) : null,
      hint: integrations.last_success ? `letzte erfolgreiche: ${new Date(integrations.last_success).toLocaleString('de-DE')}` : null,
    },
  ];
  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle>Systeminformationen</CardTitle>
        <CardDescription>Nur lesend. Zugangsdaten und Server-Adressen werden nicht angezeigt.</CardDescription>
      </CardHeader>
      <CardContent>
        <DescriptionList items={items} labelWidth="11rem" />
      </CardContent>
    </Card>
  );
}
